import { request } from "./client";
import { formatPostTimestamp } from "../utils/time";

function formatCompactCount(value) {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "0";
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(num >= 10_000_000 ? 0 : 1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(num >= 10_000 ? 0 : 1)}k`;
  return `${num}`;
}


function normalizeAuthorFrom(obj) {
  const prof = obj?.profile ?? obj?.user?.profile ?? obj?.author?.profile ?? {};
  const u = obj?.user ?? obj?.author ?? obj?.authorProfile ?? {};
  return {
    id: prof.id ?? u.id ?? obj?.userId ?? obj?.authorId ?? "",
    name: prof.fullName ?? u.fullName ?? u.name ?? "Anonymous",
    handle: prof.username
      ? `@${prof.username}`
      : u.username
        ? `@${u.username}`
        : "",
    avatar: prof.avatarUrl ?? u.avatarUrl ?? u.avatar ?? "",
    badge: prof.verifiedBadge ?? u.verifiedBadge ?? null,
  };
}

function normalizeMediaFrom(obj) {
  const media = Array.isArray(obj?.media)
    ? obj.media
    : Array.isArray(obj?.mediaUrls)
      ? obj.mediaUrls
      : [];
  const images = media
    .filter((m) => m.type === "image" || m.type === "gif")
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((m) => m.url);
  const videos = media
    .filter((m) => m.type === "video")
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((m) => m.url);
  return { images, videos };
}

function normalizePollFrom(rawPoll) {
  if (!rawPoll) return null;
  const rawOptions = rawPoll.options ?? rawPoll.Options ?? [];
  const totalVotes = rawOptions.reduce(
    (acc, o) => acc + (o.voteCount ?? o.votes ?? 0),
    0,
  );
  return {
    id: rawPoll.id,
    question: rawPoll.question,
    expiresAt: rawPoll.expiresAt ?? rawPoll.expiredAt ?? null,
    totalVotes,
    options: rawOptions.map((o) => ({
      id: o.id,
      text: o.text ?? o.label ?? o.option ?? "",
      voteCount: o.voteCount ?? o.votes ?? 0,
      pct:
        totalVotes > 0
          ? Math.round(((o.voteCount ?? o.votes ?? 0) / totalVotes) * 100)
          : 0,
    })),
    // Backend field is `userSelectedOptionId` (see PostPollResponse) — the
    // other two keys are kept as a defensive fallback for older payload
    // shapes, but without this one a real, saved vote would still render
    // as "unvoted" on the next fetch.
    userVotedOptionId:
      rawPoll.userSelectedOptionId ?? rawPoll.userVotedOptionId ?? rawPoll.votedOptionId ?? null,
  };
}

export function normalizePost(p, lookup = null) {
  if (!p) return p;

  const author = normalizeAuthorFrom(p);

  const rawLikes =
    p.likesCount ??
    p._count?.likes ??
    p.likeCount ??
    (typeof p.likes === "number" ? p.likes : 0);
  const likes = Math.max(0, Number(rawLikes) || 0);

  const liked = p.isLiked ?? p.likedByMe ?? p.liked ?? false;

  const rawComments =
    p._count?.comments ??
    p.commentCount ??
    p.commentsCount ??
    (typeof p.comments === "number" ? p.comments : 0);
  const comments = Math.max(0, Number(rawComments) || 0);

  const ownMedia = normalizeMediaFrom(p);
  const ownImages = ownMedia.images.length > 0 ? ownMedia.images : (p.images ?? []);
  const ownVideos = ownMedia.videos.length > 0 ? ownMedia.videos : (p.videos ?? []);
  const ownPoll = normalizePollFrom(p.poll ?? p.Poll ?? null);

  const isReshared = p.isResharedByMe ?? p.isReshared ?? p.resharedByMe ?? false;
  const myReshareId = p.myReshareId ?? null;
  const original = p.resharedPost ?? null;
  const isRepost = !!(original || p.type === "reshare" || p.isResharedPost);
  const originalFull = isRepost
    ? (lookup?.get(p.resharedPostId ?? original?.id) ?? null)
    : null;
  const reshareSource = isRepost ? (originalFull ?? original ?? p) : p;
  const reshares = Math.max(
    0,
    Number(
      reshareSource?.resharesCount ??
        reshareSource?.reshareCount ??
        reshareSource?._count?.reshares ??
        0,
    ) || 0,
  );

  let displayAuthor = author;
  let content = p.content ?? null;
  let images = ownImages;
  let videos = ownVideos;
  let poll = ownPoll;
  let repostedBy = null;
  let originalTimestamp = null;
  let finalLikes = likes;
  let finalLiked = liked;
  let finalComments = comments;

  if (isRepost && original) {
    repostedBy = author;
    displayAuthor = normalizeAuthorFrom(original);
    content = original.content ?? null;
    const originalMedia = normalizeMediaFrom(original);
    images = originalMedia.images.length > 0 ? originalMedia.images : (originalFull ? normalizeMediaFrom(originalFull).images : []);
    videos = originalMedia.videos.length > 0 ? originalMedia.videos : (originalFull ? normalizeMediaFrom(originalFull).videos : []);
    poll = normalizePollFrom(original.poll ?? original.Poll ?? originalFull?.poll ?? originalFull?.Poll ?? null);
    originalTimestamp = formatPostTimestamp(original.createdAt);

    const countSource = originalFull ?? original;
    const originalLikes =
      countSource.likesCount ??
      countSource._count?.likes ??
      countSource.likeCount ??
      (typeof countSource.likes === "number" ? countSource.likes : 0);
    finalLikes = Math.max(0, Number(originalLikes) || 0);
    finalLiked = countSource.isLiked ?? countSource.likedByMe ?? countSource.liked ?? false;

    const originalComments =
      countSource._count?.comments ??
      countSource.commentCount ??
      countSource.commentsCount ??
      (typeof countSource.comments === "number" ? countSource.comments : 0);
    finalComments = Math.max(0, Number(originalComments) || 0);
  }

  return {
    ...p,
    author: displayAuthor,
    content,
    likes: finalLikes,
    liked: finalLiked,
    comments: finalComments,
    reshares,
    interactionPostId: isRepost
      ? (p.resharedPostId ?? original?.id ?? p.id)
      : p.id,
    isReshared,
    myReshareId,
    isRepost,
    repostedBy,
    originalTimestamp,
    timestamp: formatPostTimestamp(p.createdAt),
    categoryId: p.categoryId ?? p.category?.id ?? null,
    categoryName: p.category?.name ?? p.categoryName ?? p.category_name ?? null,
    images,
    videos,
    poll,
  };
}

// ── Feed ──────────────────────────────────────────────────────

export const getFeedPosts = (cursor = null, limit = 30, categoryId = null, profileId = null) =>
  request(
    `/posts?limit=${limit}` +
      (cursor ? `&cursor=${encodeURIComponent(cursor)}` : "") +
      (categoryId ? `&categoryId=${encodeURIComponent(categoryId)}` : "") +
      (profileId ? `&profileId=${encodeURIComponent(profileId)}` : ""),
    { cache: "no-store" },
  ).then((data) => {
    const envelope = data?.data ?? data ?? {};
    const raw = Array.isArray(envelope)
      ? envelope
      : Array.isArray(envelope.data)
        ? envelope.data
        : (envelope.posts ?? data?.posts ?? []);
    const meta = envelope?.meta ?? data?.meta ?? data?.paginationMeta ?? {};
    const lookup = new Map(raw.filter((item) => item?.id).map((item) => [item.id, item]));
    return {
      posts: raw.map((item) => normalizePost(item, lookup)),
      hasMore: meta?.hasNextPage ?? meta?.hasMore ?? data?.hasMore ?? false,
      nextCursor:
        meta?.nextCursor ?? meta?.cursor ?? data?.nextCursor ?? data?.cursor ?? null,
    };
  });

export const getFeedCategories = () =>
  request("/categories")
    .then((data) =>
      Array.isArray(data) ? data : (data.data ?? data.categories ?? []),
    )
    .catch(() => []);

export const createCategory = (name, description = "") =>
  request("/categories", {
    method: "POST",
    body: JSON.stringify({ name, description }),
  });

export const createPost = (payload) =>
  request("/posts", { method: "POST", body: JSON.stringify(payload) }).catch(
    (err) => {
      const message = `${err?.message ?? ""}`.toLowerCase();
      if (
        message.includes("user stat not found") ||
        message.includes("user_stat") ||
        message.includes("profile stat")
      ) {
        const friendlyErr = new Error(
          "Your profile stats are not ready yet. Please refresh the page or ask the backend team to seed your account stats before posting.",
        );
        friendlyErr.status = err?.status;
        friendlyErr.path = err?.path;
        throw friendlyErr;
      }
      throw err;
    },
  );

export const updatePost = (postId, payload) =>
  request(`/posts/${postId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

export const deletePost = (postId) =>
  request(`/posts/${postId}`, { method: "DELETE" });

export const likePost = (postId) =>
  request(`/posts/${postId}/like`, { method: "POST" }).catch(() => {});

export const unlikePost = (postId) =>
  request(`/posts/${postId}/like`, { method: "POST" }).catch(() => {});

export const recordPostView = (postId) =>
  request(`/posts/${postId}/view`, { method: "POST" }).catch(() => {});

export const resharePost = async (postId) => {
  const body = await request(`/posts/${postId}/reshare`, { method: "POST" });
  return body?.data ?? body ?? null;
};

export const getTrendingTopics = async () => {
  try {
    const data = await request("/feed/trending", { cache: "no-store" });
    const envelope = data?.data ?? data ?? {};
    const raw =
      Array.isArray(envelope)
        ? envelope
        : Array.isArray(envelope.topics)
          ? envelope.topics
          : Array.isArray(envelope.items)
            ? envelope.items
            : Array.isArray(envelope.trending)
              ? envelope.trending
              : [];

    return raw
      .map((topic, index) => {
        const hashtag =
          topic.hashtag ??
          topic.tag ??
          (topic.topic ? `#${topic.topic}` : "") ??
          "";
        const title =
          topic.title ??
          topic.name ??
          topic.label ??
          (hashtag ? hashtag.replace(/^#/, "") : `Trending topic ${index + 1}`);
        const postCount =
          topic.postCount ??
          topic.posts ??
          topic.count ??
          topic.totalPosts ??
          topic.post_count ??
          0;

        return {
          id: topic.id ?? topic.slug ?? `${hashtag || title}-${index}`,
          hashtag: hashtag || `#${title}`,
          title,
          postCount: formatCompactCount(postCount),
        };
      })
      .filter((topic) => topic.hashtag || topic.title);
  } catch {
    return [];
  }
};

// ── Polls ─────────────────────────────────────────────────────

function durationToExpiresAt(duration) {
  const map = {
    "1 hour": 1 * 60 * 60 * 1000,
    "6 hours": 6 * 60 * 60 * 1000,
    "12 hours": 12 * 60 * 60 * 1000,
    "1 day": 24 * 60 * 60 * 1000,
    "3 days": 3 * 24 * 60 * 60 * 1000,
    "7 days": 7 * 24 * 60 * 60 * 1000,
  };
  const ms = map[duration] ?? 24 * 60 * 60 * 1000;
  return new Date(Date.now() + ms).toISOString();
}

export const createPoll = ({ question, options, duration, categoryId, content = "" }) =>
  request("/posts", {
    method: "POST",
    body: JSON.stringify({
      content,
      categoryId,
      isPublished: true,
      poll: {
        question,
        options,
        expiresAt: durationToExpiresAt(duration),
      },
    }),
  });

export const votePoll = (pollId, optionId) =>
  request(`/poll/${pollId}/vote`, {
    method: "POST",
    body: JSON.stringify({ optionId }),
  });

export const getPollResult = (pollId) => request(`/poll/${pollId}/result`);

// Pages through every cursor until the backend reports hasMore=false, so
// callers get the *complete* post history rather than a single page capped
// at one request's limit.
async function getAllPosts({ pageSize = 100, maxPages = 50 } = {}) {
  const all = [];
  let cursor = null;
  for (let page = 0; page < maxPages; page++) {
    const data = await getFeedPosts(cursor, pageSize);
    all.push(...(data.posts ?? []));
    cursor = data.hasMore ? (data.nextCursor ?? null) : null;
    if (!cursor) break;
  }

  const byId = new Map(all.filter((p) => p?.id).map((p) => [p.id, p]));
  for (const post of all) {
    if (!post.isRepost) continue;
    const originalId = post.resharedPostId ?? post.resharedPost?.id;
    const match = originalId ? byId.get(originalId) : null;
    if (match && match !== post) {
      post.likes = Math.max(0, Number(match.likes) || 0);
      post.liked = match.liked ?? false;
      post.comments = Math.max(0, Number(match.comments) || 0);
    }
  }

  return all;
}

async function getAllPostsForProfile(profileId, { pageSize = 100, maxPages = 50 } = {}) {
  if (!profileId) return [];
  const all = [];
  let cursor = null;
  for (let page = 0; page < maxPages; page++) {
    const data = await getFeedPosts(cursor, pageSize, null, profileId);
    all.push(...(data.posts ?? []));
    cursor = data.hasMore ? (data.nextCursor ?? null) : null;
    if (!cursor) break;
  }

  return all;
}

export const getUserReshares = async (profileId) => {
  const all = await getAllPostsForProfile(profileId);
  return all.filter(
    (post) => post.isReshared || post.resharedByMe || post.isResharedByMe || post.isRepost,
  );
};

// ── User posts ────────────────────────────────────────────────

export const getUserPosts = async (userId) => {
  if (!userId) {
    return getAllPosts();
  }

  let profileRowId = userId;
  try {
    const own = await request(`/profiles`);
    const p = own?.data ?? own ?? {};
    if (p.userId === userId || p.id === userId) {
      profileRowId = p.id ?? userId;
    } else {
      const allRaw = await request(`/profiles/all`);
      const list = Array.isArray(allRaw?.data)
        ? allRaw.data
        : Array.isArray(allRaw)
          ? allRaw
          : [];
      const match = list.find((u) => u.userId === userId || u.id === userId);
      if (match) profileRowId = match.id ?? match.userId ?? userId;
    }
  } catch { /* keep profileRowId = userId */ }

  return getAllPostsForProfile(profileRowId);
};